import React, { useState } from 'react';
import { useLang } from '../context/LanguageContext';
import { useContent } from '../context/ContentContext';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';

const Reviews = () => {
  const { t, lang } = useLang();
  const { content } = useContent();
  const reviews = content?.reviews || [];
  const [idx, setIdx] = useState(0);

  const safeIdx = reviews.length ? idx % reviews.length : 0;
  const r = reviews[safeIdx];

  const next = () => setIdx((i) => (i + 1) % Math.max(reviews.length, 1));
  const prev = () => setIdx((i) => (i - 1 + reviews.length) % Math.max(reviews.length, 1));

  return (
    <section id="reviews" className="section-pad bg-[#FAF8F4] border-y border-[#E8E2D5]">
      <div className="container-tight">
        <div className="grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <span className="eyebrow left">{t.reviews.eyebrow}</span>
            <h2 className="h-display mt-6" style={{ fontSize: 'clamp(36px, 4.5vw, 56px)' }}>
              {t.reviews.title}
            </h2>

            <div className="mt-12 border-t border-[#E8E2D5] pt-8">
              <div className="flex items-baseline gap-3">
                <div className="display text-[64px] leading-none text-[#957340]">4.2</div>
                <div>
                  <div className="flex gap-1">
                    {[1,2,3,4,5].map((s) => (
                      <Star key={s} size={14} className={s <= 4 ? 'fill-[#B8945B] text-[#B8945B]' : 'text-[#B8945B]'} />
                    ))}
                  </div>
                  <div className="text-[10px] tracking-[0.28em] uppercase text-[#8A8275] mt-2">{t.reviews.rating}</div>
                </div>
              </div>
              <div className="text-[12px] tracking-[0.2em] uppercase text-[#8A8275] mt-4">201 {t.reviews.total}</div>
            </div>
          </div>

          <div className="lg:col-span-8">
            <div className="bg-white border border-[#E8E2D5] p-10 md:p-14 relative min-h-[340px]">
              <div className="display text-[120px] leading-none text-[#B8945B]/20 absolute top-6 left-10">&ldquo;</div>
              {r ? (
                <div className="relative">
                  <div className="flex gap-1 mb-6">
                    {[...Array(r.rating)].map((_, i) => (
                      <Star key={`star-${r.id}-${i}`} size={14} className="fill-[#B8945B] text-[#B8945B]" />
                    ))}
                  </div>
                  <p className="serif text-[22px] md:text-[26px] leading-[1.5] text-[#1A1A1A]">
                    {lang === 'fr' ? r.textFr : r.textEn}
                  </p>
                  <div className="mt-10 flex items-center justify-between border-t border-[#E8E2D5] pt-6">
                    <div>
                      <div className="text-[14px] font-medium tracking-wide">{r.name}</div>
                      <div className="text-[10px] tracking-[0.28em] uppercase text-[#8A8275] mt-1">
                        {lang === 'fr' ? r.dateFr : r.dateEn}
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button onClick={prev} className="w-11 h-11 border border-[#1A1A1A] flex items-center justify-center hover:bg-[#1A1A1A] hover:text-white transition-colors" aria-label="prev">
                        <ChevronLeft size={16} />
                      </button>
                      <button onClick={next} className="w-11 h-11 border border-[#1A1A1A] flex items-center justify-center hover:bg-[#1A1A1A] hover:text-white transition-colors" aria-label="next">
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-[#8A8275] text-center py-12">…</div>
              )}
            </div>

            {reviews.length > 0 && (
              <div className="flex gap-2 mt-6">
                {reviews.map((rev, i) => (
                  <button
                    key={`pag-${rev.id}`}
                    onClick={() => setIdx(i)}
                    className={`h-[2px] transition-all duration-500 ${i === safeIdx ? 'w-12 bg-[#957340]' : 'w-6 bg-[#E8E2D5]'}`}
                    aria-label={`review ${i+1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
